import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import moment from "moment";
import {
  HiOutlineEnvelope, HiOutlineClock, HiOutlineSquares2X2, HiOutlineCheckCircle, HiOutlineUsers,
  HiOutlineBuildingOffice2, HiOutlineCreditCard, HiOutlineClipboardDocumentCheck, HiOutlinePaperAirplane,
  HiOutlineShoppingCart, HiOutlineAdjustmentsHorizontal
} from "react-icons/hi2";

import { userAtom } from "../../../services/atoms/auth.atoms";
import { ROUTES } from "../../../helpers/constants";
import { colorsFromResults } from "../../../helpers/functions";
import { getEmailStatusInfo, getLatestTest, isTestFinished } from "../../../utils/test-invitation-link";
import { TestTypeService } from "../../../utils/test-type-registry";
import ClientAPI from "../../../services/api/clients.api";
import CollaboratorsAPI from "../../../services/api/collaborators.api";
import GuestPaymentsAPI from "../../../services/api/guest-payments.api";
import ColorsCircles from "../params/components/colors-roles";
import { featureToggles } from "../../../utils/featureToggles";

// Página de bienvenida del panel. Solo lee datos con los servicios que ya existen;
// no modifica nada. Para un cliente resume su empresa; para superadmin, la cartera.

const UNOPENED_DAYS = 3;
const LOW_TESTS = 5;
const COLLABORATORS_SAMPLE = 500;
const canPurchase = () => featureToggles.isEnabled('enablePurchaseSection');

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
};

const firstName = (name = '') => name.trim().split(/\s+/)[0] || '';

// Mismos dos colores que muestra la tabla de colaboradores: Test Growme (unificado
// o competencias) y, si no hay, el DISC.
const resultColors = (tests = []) => {
  const growme = tests.find((t) => Number(t.statusId) >= 4 && (TestTypeService.isTestCategory(t, 'unified') || TestTypeService.isTestCategory(t, 'competency')));
  if (growme?.results) return colorsFromResults(growme.results, false, true);
  const disc = tests.find((t) => Number(t.statusId) >= 4 && TestTypeService.isTestCategory(t, 'disc'));
  if (disc?.results) return colorsFromResults(disc.results, tests.length === 2, false);
  return ['none', 'none'];
};

const daysWithoutOpening = (test) => {
  if (![1, 2].includes(Number(test?.statusId)) || !test?.sentAt) return 0;
  return moment().diff(moment(test.sentAt), 'days');
};

const Kpi = ({ icon: Icon, label, value, foot }) => (
  <div className="gm-kpi">
    <div className="gm-kpi-label"><Icon size={16} />{label}</div>
    <div className="gm-kpi-value">{value}</div>
    {foot && <div className="gm-kpi-foot">{foot}</div>}
  </div>
);

const AttentionItem = ({ tone, icon: Icon, title, description, action }) => (
  <div className="gm-att-item">
    <span className={`gm-att-ic gm-att-ic--${tone}`}><Icon size={20} /></span>
    <div className="gm-att-text"><b>{title}</b><span>{description}</span></div>
    {action}
  </div>
);

const QuickAction = ({ icon: Icon, label, onClick, main }) => (
  <button type="button" className={`gm-quick${main ? ' gm-quick--main' : ''}`} onClick={onClick}>
    <span className="gm-quick-ic"><Icon size={18} /></span>
    {label}
  </button>
);

const Home = () => {
  const navigate = useNavigate();
  const user = useRecoilValue(userAtom)?.data || {};
  const isSuperAdmin = !!user.isSuperAdmin;
  const clientId = user.clientId;

  const [client, setClient] = useState(null);
  const [collaborators, setCollaborators] = useState([]);
  const [clients, setClients] = useState({ list: [], total: 0 });
  const [pendingPayments, setPendingPayments] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        if (isSuperAdmin) {
          const [clientsRes, paymentsRes] = await Promise.allSettled([
            ClientAPI.clientsList({ paginationParams: { page: 1, limit: 100 } }),
            GuestPaymentsAPI.list(),
          ]);
          if (!active) return;
          if (clientsRes.status === 'fulfilled') {
            const data = clientsRes.value?.data;
            setClients({ list: data?.data || [], total: data?.total || (data?.data || []).length });
          }
          if (paymentsRes.status === 'fulfilled') {
            const payments = paymentsRes.value?.data;
            setPendingPayments(Array.isArray(payments) ? payments.length : (payments?.data || []).length);
          }
        } else if (clientId) {
          const [clientRes, collabRes] = await Promise.allSettled([
            ClientAPI.findClient(clientId),
            CollaboratorsAPI.collaboratorsList({ clientId, paginationParams: { page: 1, limit: COLLABORATORS_SAMPLE, sort: 'finished_recent' } }),
          ]);
          if (!active) return;
          if (clientRes.status === 'fulfilled') setClient(clientRes.value?.data || null);
          if (collabRes.status === 'fulfilled') setCollaborators(collabRes.value?.data?.data || []);
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [isSuperAdmin, clientId]);

  // Resumen calculado con el test vigente de cada colaborador (mismo criterio que la tabla).
  const summary = useMemo(() => {
    const result = { pending: 0, bounced: [], unopened: 0, finished: 0, recent: [] };
    collaborators.forEach((collaborator) => {
      const tests = collaborator.latestTest || [];
      const latest = getLatestTest(tests);
      if (!latest) return;
      if (isTestFinished(latest)) {
        result.finished += 1;
        result.recent.push({
          collaborator,
          test: latest,
          date: latest.finishedAt || latest.updatedAt || latest.createdAt,
          colors: resultColors(tests),
        });
        return;
      }
      result.pending += 1;
      if (getEmailStatusInfo(latest)?.problem) result.bounced.push(collaborator);
      else if (daysWithoutOpening(latest) >= UNOPENED_DAYS) result.unopened += 1;
    });
    result.recent.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    result.recent = result.recent.slice(0, 6);
    return result;
  }, [collaborators]);

  const goToCompany = () => navigate(clientId ? `${ROUTES.CLIENTS.route}/${clientId}` : ROUTES.CLIENTS.route, client ? { state: { client } } : undefined);
  const openCollaborator = (collaborator) =>
    navigate(`${ROUTES.CLIENTS.route}/collaborator/${collaborator.id}`, { state: { collaborator, client } });

  const today = new Date().toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
  const name = firstName(user.name || user.displayName);

  const attention = [];
  if (!isSuperAdmin) {
    if (summary.bounced.length) {
      const names = summary.bounced.slice(0, 3).map((c) => `${c.name} ${c.surname || ''}`.trim()).join(', ');
      attention.push(
        <AttentionItem key="bounced" tone="danger" icon={HiOutlineEnvelope}
          title={summary.bounced.length === 1 ? 'Un correo de invitación no llegó' : `${summary.bounced.length} correos de invitación no llegaron`}
          description={`${names}${summary.bounced.length > 3 ? ' y otros' : ''}. Envíales el test desde tu correo, con el link o por WhatsApp.`}
          action={<button type="button" className="gm-secondary-button" onClick={goToCompany}>Revisar</button>} />
      );
    }
    if (summary.unopened) {
      attention.push(
        <AttentionItem key="unopened" tone="warn" icon={HiOutlineClock}
          title={`${summary.unopened} ${summary.unopened === 1 ? 'test lleva' : 'tests llevan'} más de ${UNOPENED_DAYS} días sin abrirse`}
          description="Un recordatorio suele bastar. Puedes reenviarlo desde la lista de colaboradores."
          action={<button type="button" className="gm-secondary-button" onClick={goToCompany}>Ver colaboradores</button>} />
      );
    }
    if (client && canPurchase() && Number(client.testsAvailable || 0) <= LOW_TESTS) {
      attention.push(
        <AttentionItem key="tests" tone="info" icon={HiOutlineSquares2X2}
          title={`Te ${Number(client.testsAvailable) === 1 ? 'queda 1 test disponible' : `quedan ${client.testsAvailable || 0} tests disponibles`}`}
          description="Compra más tests para seguir enviando invitaciones."
          action={<button type="button" className="gm-secondary-button" onClick={() => navigate(ROUTES.PURCHASE.route, { state: { client } })}>Comprar tests</button>} />
      );
    }
  } else if (pendingPayments) {
    attention.push(
      <AttentionItem key="payments" tone="warn" icon={HiOutlineCreditCard}
        title={`${pendingPayments} ${pendingPayments === 1 ? 'pago por asignar' : 'pagos por asignar'}`}
        description="Compras hechas sin cuenta que debes vincular a un cliente."
        action={<button type="button" className="gm-secondary-button" onClick={() => navigate(ROUTES.GUEST_PAYMENTS.route)}>Asignar</button>} />
    );
  }

  return (
    <div className="gm-home">
      <header className="gm-hero">
        <div>
          <div className="gm-hero-date">{today}</div>
          <h1>{greeting()}{name ? `, ${name}` : ''}</h1>
          <p>{isSuperAdmin ? 'Esto es lo que está pasando en GrowMe.' : `Así va ${client?.name || 'tu empresa'}.`}</p>
        </div>
        <div className="gm-hero-actions">
          {isSuperAdmin
            ? <button type="button" className="gm-primary-button" onClick={() => navigate(ROUTES.CLIENTS.route)}><HiOutlineBuildingOffice2 size={16} />Ver clientes</button>
            : <button type="button" className="gm-primary-button" onClick={goToCompany}><HiOutlinePaperAirplane size={16} />Invitar colaboradores</button>}
        </div>
      </header>

      {isSuperAdmin ? (
        <section className="gm-kpis">
          <Kpi icon={HiOutlineBuildingOffice2} label="Clientes" value={loading ? '–' : clients.total} />
          <Kpi icon={HiOutlineCreditCard} label="Pagos por asignar" value={loading || pendingPayments === null ? '–' : pendingPayments} />
        </section>
      ) : (
        <section className="gm-kpis">
          <Kpi icon={HiOutlineSquares2X2} label="Tests disponibles" value={loading ? '–' : (client?.testsAvailable ?? 0)} />
          <Kpi icon={HiOutlinePaperAirplane} label="Tests enviados" value={loading ? '–' : (client?.testsDone ?? 0)} />
          <Kpi icon={HiOutlineCheckCircle} label="Tests finalizados" value={loading ? '–' : summary.finished} foot="Según el último test de cada persona" />
          <Kpi icon={HiOutlineClock} label="Sin responder" value={loading ? '–' : summary.pending} />
        </section>
      )}

      <div className="gm-home-grid">
        <div className="gm-home-col">
          <section className="gm-panel">
            <div className="gm-panel-head"><h2>Requiere atención</h2></div>
            <div className="gm-att">
              {loading && <div className="gm-att-item gm-att-loading">Cargando…</div>}
              {!loading && attention}
              {!loading && !attention.length && (
                <AttentionItem tone="ok" icon={HiOutlineCheckCircle} title="Todo al día" description="No hay nada pendiente por resolver ahora." />
              )}
            </div>
          </section>

          {isSuperAdmin ? (
            <section className="gm-panel">
              <div className="gm-panel-head">
                <h2>Clientes</h2>
                <button type="button" className="gm-ghost-button" onClick={() => navigate(ROUTES.CLIENTS.route)}>Ver todos</button>
              </div>
              <ul className="gm-feed">
                {loading && <li className="gm-feed-empty">Cargando…</li>}
                {!loading && !clients.list.length && <li className="gm-feed-empty">Aún no hay clientes.</li>}
                {clients.list.slice(0, 6).map((c) => (
                  <li key={c.id} className="is-clickable" onClick={() => navigate(`${ROUTES.CLIENTS.route}/${c.id}`, { state: { client: c } })}>
                    <span className="gm-avatar gm-avatar--square">{(c.name || '?').slice(0, 2).toUpperCase()}</span>
                    <div className="gm-feed-text"><b>{c.name}</b><span className="gm-feed-when">{c.sector || 'Rubro no informado'}</span></div>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <section className="gm-panel">
              <div className="gm-panel-head">
                <h2>Últimos resultados</h2>
                <button type="button" className="gm-ghost-button" onClick={goToCompany}>Ver todos</button>
              </div>
              <ul className="gm-feed">
                {loading && <li className="gm-feed-empty">Cargando…</li>}
                {!loading && !summary.recent.length && <li className="gm-feed-empty">Todavía no hay tests finalizados.</li>}
                {summary.recent.map(({ collaborator, test, date, colors }) => (
                  <li key={collaborator.id} className="is-clickable" onClick={() => openCollaborator(collaborator)}>
                    <span className="gm-feed-dots">{colors.map((color, i) => <ColorsCircles key={`${color}-${i}`} colors={color} />)}</span>
                    <div className="gm-feed-text">
                      <b>{`${collaborator.name} ${collaborator.surname || ''}`.trim()}</b> finalizó {test.testName || 'su test'}
                      <span className="gm-feed-when">{[collaborator.position || collaborator.area, date ? moment(date).format('DD-MM-YYYY') : null].filter(Boolean).join(' · ')}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="gm-home-col">
          <section className="gm-panel">
            <div className="gm-panel-head"><h2>Accesos rápidos</h2></div>
            <div className="gm-quick-grid">
              {isSuperAdmin ? (
                <>
                  <QuickAction main icon={HiOutlineBuildingOffice2} label="Clientes" onClick={() => navigate(ROUTES.CLIENTS.route)} />
                  <QuickAction icon={HiOutlineClipboardDocumentCheck} label="Evaluaciones" onClick={() => navigate(ROUTES.PROCESS.route)} />
                  <QuickAction icon={HiOutlineAdjustmentsHorizontal} label="Parámetros" onClick={() => navigate(ROUTES.PARAMS.route)} />
                  <QuickAction icon={HiOutlineCreditCard} label="Pagos por asignar" onClick={() => navigate(ROUTES.GUEST_PAYMENTS.route)} />
                </>
              ) : (
                <>
                  <QuickAction main icon={HiOutlinePaperAirplane} label="Invitar colaboradores" onClick={goToCompany} />
                  <QuickAction icon={HiOutlineUsers} label="Ver colaboradores" onClick={goToCompany} />
                  {canPurchase() && <QuickAction icon={HiOutlineShoppingCart} label="Comprar tests" onClick={() => navigate(ROUTES.PURCHASE.route, { state: { client } })} />}
                </>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Home;
